/**
 * Records audio from the user's microphone with real-time chunk streaming.
 * Push-to-talk style: call start() when the user presses, stop() when they release.
 *
 * Audio is captured as PCM16 at 16kHz mono — the format Deepgram expects.
 * The onChunk callback fires continuously with audio data for streaming transcription.
 * The onLevel callback fires with a 0–1 audio volume level for visualizations.
 */
export class VoiceRecorder {
  private audioContext: AudioContext | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private processor: ScriptProcessorNode | null = null
  private stream: MediaStream | null = null
  private _isRecording = false
  private onChunk: ((chunk: ArrayBuffer) => void) | null = null
  private onLevel: ((level: number) => void) | null = null

  /**
   * Start recording.
   * @param onChunk fires with PCM16 ArrayBuffers as audio is captured
   * @param onLevel fires with a 0–1 volume level for each audio frame
   */
  async start(
    onChunk: (chunk: ArrayBuffer) => void,
    onLevel?: (level: number) => void,
  ): Promise<void> {
    this.onChunk = onChunk
    this.onLevel = onLevel ?? null

    // getUserMedia rejects when the mic is denied (NotAllowedError) or absent.
    // Set _isRecording only AFTER it resolves, and reset callbacks on failure,
    // so a denied prompt doesn't leave the recorder half-armed. The rejection
    // propagates to the caller, which resets its own UI state.
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      })
    } catch (err) {
      this.onChunk = null
      this.onLevel = null
      throw err
    }

    this._isRecording = true
    this.audioContext = new AudioContext({ sampleRate: 16000 })
    this.source = this.audioContext.createMediaStreamSource(this.stream)

    this.processor = this.audioContext.createScriptProcessor(4096, 1, 1)
    this.processor.onaudioprocess = (e) => {
      if (!this._isRecording || !this.onChunk) return
      const float32 = e.inputBuffer.getChannelData(0)

      // Calculate RMS volume level (0–1)
      if (this.onLevel) {
        let sum = 0
        for (let i = 0; i < float32.length; i++) {
          sum += float32[i] * float32[i]
        }
        const rms = Math.sqrt(sum / float32.length)
        // Scale up and clamp — raw RMS is usually 0–0.1 for speech
        this.onLevel(Math.min(1, rms * 5))
      }

      // Convert Float32 [-1,1] to Int16 PCM
      const pcm16 = new Int16Array(float32.length)
      for (let i = 0; i < float32.length; i++) {
        const s = Math.max(-1, Math.min(1, float32[i]))
        pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff
      }
      this.onChunk(pcm16.buffer)
    }

    this.source.connect(this.processor)
    this.processor.connect(this.audioContext.destination)
  }

  stop(): void {
    this._isRecording = false
    this.onChunk = null
    this.onLevel = null
    this.cleanup()
  }

  get isRecording(): boolean {
    return this._isRecording
  }

  cancel(): void {
    this._isRecording = false
    this.onChunk = null
    this.onLevel = null
    this.cleanup()
  }

  private cleanup(): void {
    this.processor?.disconnect()
    this.source?.disconnect()
    this.stream?.getTracks().forEach((t) => t.stop())
    this.audioContext?.close()
    this.processor = null
    this.source = null
    this.stream = null
    this.audioContext = null
  }
}
